import React, { useEffect, useState } from 'react'
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { notification, Spin } from 'antd';
import Modal from "../../components/uiComponent/modal/index";
import Stepper from '../../components/uiComponent/newStepper';
import Button from "../../components/uiComponent/button/index";
import StepsData from "./stepsData";
import { extend, isEmpty, trim } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { GetPresignedUrl  } from '../../slices/aws-services';
import { setItem } from '../../slices/auth';
import { BulkAdjustInventory } from '../../slices/location-management'


import icon from "../../assets/icons/close-icon-blue.svg"
import { AddProductManualModalWrapper } from './style';

const INVENTORY_ADJUSTMENT_OPTIONS = [
  { label: 'SKU', value: 'SKU', important: true, optional: false },
  { label: 'Quantity', value: 'Quantity', important: true, optional: false },
];
const sampleFileLink = "https://opulant-storage.s3.us-east-1.amazonaws.com/Bulk-Inventory-Adjustment-Template-File.xlsx"
const AddManualProduct = ({ addProduct, setAddProduct, refreshGridClick }) => {
  const dispatch = useDispatch()

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState({ rows: [], columns: [] });
  const [requiredColumns, setRequiredColumns] = useState(INVENTORY_ADJUSTMENT_OPTIONS);
  const data = requiredColumns.map((col) => ({ label: col.label, data: [] }));
  const [dataToProceed, setDataToProceed] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [fileRows, setFileRows] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [isError ,setIsError] = useState(false)
  const [selectedUser, setSelectedUser] = useState({})

  // Read currentUser from auth to determine role-based behavior
  const { currentUser } = useSelector((state) => state.auth || {});
  const isSellerPlusWarehouse = currentUser?.role?.id === 5;



  const steps = [
    { title: 'Step 1', description: 'Upload File' },
    { title: 'Step 2', description: 'Data Mapping' },
    { title: 'Step 3', description: 'Review Errors' },
  ];
  const hasMissingValues = (fileRows) => {
    if (!fileRows?.length) return true;
    if (
      !fileRows?.length ||
      fileRows.every(row => !row['SKU']) ||
      fileRows.every(row => !row['Quantity']) ||
      fileRows.every(row => !row['Quantity'])
    ) {
      return true;
    }
    else return false
  };

  const handleDisabled = () => {
    if (
      currentStep === 0 &&
      (!fileList?.length)
    ) {
      return true;
    }
    if (currentStep === 1 && hasMissingValues(fileRows)) return true;
    if (
      currentStep === 2 &&
      fileRows.every((row) => row.reason)
    ) {
      return true;
    }
    if (currentStep === 3) {
      return true;
    }

    return false;
  }; 

  const setFileColumnsToUse = (columns) => {
    const cols = columns.map((col) => ({ label: col, value: col }));
    setFileColumns(cols);
  };
  
  const parseFileData = () => {
    const parsedRows = [];
    const skuTracker = new Map(); // Map to track SKU occurrences

    fileRows.forEach(row => {
      const allKeys = Object.keys(row);
      for (const key of allKeys) {
        if (key === 'Quantity' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
          extend(row, { reason: 'Quantity must be a Number' });
        }
        if(key === 'Quantity' && !row[key]){
          extend(row, { reason: 'Quantity is required' });
        }
        if(key === 'SKU' && !row[key]){
          extend(row, { reason: 'SKU is required' });
        }
      }
      if (row.SKU) {
        if (skuTracker.has(row.SKU)) {
            // Mark all duplicates (including the first occurrence)
            skuTracker.get(row.SKU).reason = 'Duplicate SKU value';
            extend(row, { reason: 'Duplicate SKU value' });
        } else {
            skuTracker.set(row.SKU, row);
        }
    }

      parsedRows.push(row);
    });
    parsedRows.filter((row) => row.reason).length > 0 ? setIsError(true) :  setIsError(false);
    setFileRows(parsedRows);
  };
  
  const handleImportFile = () => {
    const requiredColumn = (requiredColumns.filter(col => col.require)).map(col => col.label);
    const filteredData = dataToProceed.filter(row => requiredColumn.includes(row.label) && every(row.data, value => value === ''));
    if (!isEmpty(filteredData)) {
      notification.error({
        message: 'Error',
        error: `${filteredData[0].label} is required!`
      })
    } else {
      parseFileData();
    }
  };
  const convertToXlsx = (fileData) => {
    const parsedData = typeof fileData === 'string' ? Papa.parse(fileData, { header: true }).data : fileData;
    const worksheet = XLSX.utils.json_to_sheet(parsedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const xlsxBinary = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([xlsxBinary], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return blob;
  };

  const handleSubmitFile = async () => {
    let uploadedFile = {};
    if (!isEmpty(fileList[0])) {
      const fileData = fileRows.filter(row => !row.reason);
      const fileType = fileList[0]?.name.split('.').pop().toLowerCase();
      let data = {};
      if (fileType === 'csv') {
        data = Papa.unparse(fileData);
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        data = convertToXlsx(fileData);
      } else {
        notification.error({
          message: 'File Type Error',
          description: 'Unsupported File Type'
        });
        return;
      }

      setFileUploading(true);
      dispatch(setItem({
        field: 'showFileHeader',
        value: false
      }));
      await dispatch(GetPresignedUrl({ files: [fileList[0]], fileData: data })).then(({ payload }) => {
        if (!payload?.error && !payload?.error?.error && payload) {
          uploadedFile = payload[0];
          if (uploadedFile) {
            const selectedUserId = isSellerPlusWarehouse && !currentUser?.is_employee_of ? currentUser?.id : isSellerPlusWarehouse && currentUser?.is_employee_of ? currentUser.is_employee_of : selectedUser?.value;
            dispatch(BulkAdjustInventory({fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, selectedUser: selectedUserId})).then(({payload})=>{
             setFileUploading(false)
            })
          }
        } else {
          setFileUploading(false);
        }
        dispatch(setItem({
          field: 'showFileHeader',
          value: true
        }));
      });
    }
  };

  const clearStates = () => {
    setFileData({ rows: [], columns: [] });
    setFileRows([]);
    setFileColumns([]);
    setFileList([]);
    setCurrentStep(0);
    setAddProduct(false);
    refreshGridClick();
    setSelectedUser({});
  };
  
  const nextStep = async () => {
    if (currentStep === 0 && isEmpty(dataToProceed)) {
      const { rows = [], columns = [] } = fileData;
      if (rows?.length) {
        const matchedCols = [];
        const labels = Object.keys(rows[0]);
        const mappedData = [];
        requiredColumns?.forEach(({ label, important = false, optional = true }) => {
          const item = labels?.find(option => option === label);
          let dataToPush = { label, data: [], important, optional };
          
          if (!isEmpty(item)) {
            dataToPush = { label, value: label, data: rows.map(item => trim(item[label]) || ''), important, optional };
            matchedCols.push(item);
          }
          mappedData.push(dataToPush);
        });
        setDataToProceed(mappedData)
        setFileColumnsToUse(columns);
      } else setDataToProceed([]);
    }
    if (currentStep === 1) {
      handleImportFile();
    }
    if (currentStep === 2) {
      handleSubmitFile();
    }
    currentStep !== 3 && setCurrentStep(currentStep + 1);
  };
  return (
    <div>
      <Modal
        open={addProduct}
        onClose={() => setAddProduct(false)}
        title={null}
        header={null}
        footer={null}
        className="add-product-flow"
        closeButton={false}
        width={808}
        onHide={null}
        onSave={() => {
          setAddProduct(false)
        }}
      >
        <AddProductManualModalWrapper>
          <div className='header-wrapper'><span
          >Bulk inventory adjustment</span><img onClick={() => clearStates()} className='cursor-pointer' src={icon} /></div>
          <Stepper
            currentStep={currentStep}
            steps={steps}
            direction="horizontal"
            size="default"
            status="process"
          />
          <Spin tip='Loading..' spinning={fileUploading}>
          <StepsData
           setAddProduct={setAddProduct}
           setDataToProceed={setDataToProceed}
           currentStep={currentStep}
           setCurrentStep={setCurrentStep}
           setFileData={setFileData}
           setFileList={setFileList}
           setFileColumns={setFileColumns}
           setFileRows={setFileRows}
           setSelectedUser={setSelectedUser}
           dataToProceed={dataToProceed}
           fileData={fileData}
           fileRows={fileRows}
           fileList={fileList}
           fileColumns={fileColumns}
           clearStates={clearStates}
           setIsError={setIsError}
           sampleFileLink={sampleFileLink}
           />
           </Spin>
          {currentStep === 3 ? null :<footer>
            <Button
              text={currentStep === 2 ? 'Re-Upload File' : 'Back'}
              className="outlined"
              onClick={() => {
                if(currentStep === 2){
                  setFileData({ rows: [], columns: [] });
                  setFileRows([]);
                  setFileColumns([]);
                  setFileList([]);
                  setCurrentStep(0);
                  setSelectedUser({});
                }
                else if(currentStep === 0){
                  setFileList([]);
                  setAddProduct(false)
                }
                else{
                  setSelectedUser({});
                  setCurrentStep(currentStep - 1)
                }
              }}
            />
            <Button
              text={currentStep === 2  && !isError ? 'Proceed' : currentStep === 2  && isError ? 'Discord Errors & Proceed': 'Next'}
              onClick={()=> nextStep()}
              disabled={(currentStep === 3  || handleDisabled()) || (currentStep === 1 && !isSellerPlusWarehouse && Object.keys(selectedUser).length === 0)}
            />
          </footer>}
        </AddProductManualModalWrapper>
      </Modal>

    </div>
  )
}

export default AddManualProduct;
